import React, { useState } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Search, Loader2, Package, Factory, Truck, AlertTriangle,
  CheckCircle2, Clock, ArrowRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

const TXN_TYPE_LABELS = {
  receive: 'Received',
  issue: 'Issued',
  qc_accept_in: 'QC Accept (In)',
  qc_accept_out: 'QC Accept (Out)',
  qc_quarantine_in: 'QC Quarantine (In)',
  qc_quarantine_out: 'QC Quarantine (Out)',
  qc_reject_in: 'QC Reject (In)',
  qc_reject_out: 'QC Reject (Out)',
  mrb_reject_out: 'MRB Reject (Out)',
  mrb_reject_in: 'MRB Reject (In)',
  mrb_reaccept_out: 'MRB Re-accept (Out)',
  mrb_reaccept_in: 'MRB Re-accept (In)',
  production_consume: 'BOM Consumed',
  production_output: 'Production Output',
};

const TXN_TYPE_COLORS = {
  receive: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  issue: 'bg-red-50 text-red-700 border-red-200',
  qc_accept_in: 'bg-blue-50 text-blue-700 border-blue-200',
  qc_accept_out: 'bg-blue-50 text-blue-600 border-blue-200',
  qc_quarantine_in: 'bg-amber-50 text-amber-700 border-amber-200',
  qc_quarantine_out: 'bg-amber-50 text-amber-600 border-amber-200',
  qc_reject_in: 'bg-red-50 text-red-600 border-red-200',
  qc_reject_out: 'bg-red-50 text-red-600 border-red-200',
};

const WO_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  released: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  completed: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-slate-100 text-slate-500',
};

export default function TraceReportPage() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    challan: true, wo: true, availability: true, transactions: false, balances: true, dispatch: true, mrb: true,
  });

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSearch = async () => {
    if (!query.trim()) { toast.error('Enter a trace ID, WO number, or challan number'); return; }
    setLoading(true);
    try {
      const res = await inventoryAPI.traceConsumptionReport(query.trim());
      setData(res.data);
      if (!res.data.challan_lines?.length && !res.data.work_orders?.length) {
        toast.info('No results found for this search term');
      }
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const SectionHeader = ({ title, icon: Icon, sectionKey, count, color = 'text-blue-700' }) => (
    <button
      className="flex items-center justify-between w-full p-3 hover:bg-muted/30 transition-colors"
      onClick={() => toggleSection(sectionKey)}
      data-testid={`trace-section-${sectionKey}`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-sm font-semibold">{title}</span>
        {count !== undefined && <Badge variant="outline" className="text-[10px] ml-1">{count}</Badge>}
      </div>
      {expandedSections[sectionKey] ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="trace-report-page">
      <div>
        <h1 className="font-heading text-xl font-bold tracking-tight">Trace Consumption Report</h1>
        <p className="text-xs text-muted-foreground">Search by Customer WO#, Trace ID, Work Order#, or Challan# to see full consumption audit trail</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-3">
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Enter Customer WO#, Trace ID, WO number, or Challan#..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="h-9 text-sm pl-8"
                data-testid="trace-report-search"
              />
            </div>
            <Button onClick={handleSearch} disabled={loading} className="h-9" data-testid="trace-report-search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <div className="space-y-3">
          {/* ===== INBOUND CHALLAN RECEIPT ===== */}
          {data.challan_lines?.length > 0 && (
            <Card>
              <SectionHeader title="Inbound Receipt" icon={Package} sectionKey="challan" count={data.challan_lines.length} color="text-purple-700" />
              {expandedSections.challan && (
                <CardContent className="p-0 pt-0">
                  <table className="w-full text-xs">
                    <thead><tr className="border-t bg-muted/30">
                      <th className="text-left p-2 font-medium text-muted-foreground">Challan</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Item</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Trace ID</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Received</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">OK</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Rejected</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Quarantined</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">QC Status</th>
                    </tr></thead>
                    <tbody>
                      {data.challan_lines.map((cl, i) => (
                        <tr key={i} className="border-t hover:bg-muted/20">
                          <td className="p-2">
                            <p className="font-medium font-mono">{cl.challan_number}</p>
                            <p className="text-[10px] text-muted-foreground">Line #{cl.line_index + 1} | DC: {cl.customer_challan_number || '-'}</p>
                          </td>
                          <td className="p-2">
                            <p className="font-medium">{cl.item_name}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{cl.item_unique_id}</p>
                          </td>
                          <td className="p-2 font-mono font-medium text-purple-700">{cl.traceability_id || '-'}</td>
                          <td className="p-2 text-center font-medium">{cl.quantity}</td>
                          <td className="p-2 text-center font-medium text-emerald-700">{cl.ok_quantity}</td>
                          <td className="p-2 text-center font-medium text-red-600">{cl.rejected_quantity || 0}</td>
                          <td className="p-2 text-center font-medium text-amber-600">{cl.quarantined_quantity || 0}</td>
                          <td className="p-2 text-center">
                            <Badge variant="outline" className={`text-[10px] ${cl.quality_status === 'qc_completed' ? 'text-emerald-600 border-emerald-200' : 'text-amber-600 border-amber-200'}`}>
                              {cl.quality_status === 'qc_completed' ? 'Completed' : cl.quality_status || 'Pending'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          )}

          {/* ===== AVAILABILITY ANALYSIS ===== */}
          {data.availability_analysis?.length > 0 && (
            <Card className="border-blue-200">
              <SectionHeader title="Availability Analysis (Why not available for WO?)" icon={AlertTriangle} sectionKey="availability" count={data.availability_analysis.length} color="text-blue-700" />
              {expandedSections.availability && (
                <CardContent className="p-3 pt-0 space-y-3">
                  {data.availability_analysis.map((a, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg border space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs font-medium">{a.item_name}</span>
                          <span className="text-[10px] text-muted-foreground ml-2">({a.challan_number} Line #{a.line_index + 1})</span>
                          {a.traceability_id && <Badge variant="outline" className="ml-2 text-[10px] text-purple-700 border-purple-200 font-mono">{a.traceability_id}</Badge>}
                        </div>
                        <Badge className={`text-xs ${a.available_for_new_wo > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                          Available: {a.available_for_new_wo}
                        </Badge>
                      </div>
                      {/* Flow breakdown */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[10px]">
                        <div className="p-1.5 bg-white rounded border text-center">
                          <p className="text-muted-foreground">Received</p>
                          <p className="font-bold text-sm">{a.total_received}</p>
                        </div>
                        <div className="p-1.5 bg-white rounded border text-center">
                          <p className="text-muted-foreground">QC OK</p>
                          <p className="font-bold text-sm text-emerald-700">{a.ok_quantity}</p>
                        </div>
                        <div className="p-1.5 bg-white rounded border text-center">
                          <p className="text-muted-foreground">WO Reserved</p>
                          <p className="font-bold text-sm text-blue-700">{a.total_reserved_by_wos}</p>
                        </div>
                        <div className="p-1.5 bg-white rounded border text-center">
                          <p className="text-muted-foreground">Actually Consumed</p>
                          <p className="font-bold text-sm text-amber-700">{a.total_actually_consumed}</p>
                        </div>
                        <div className="p-1.5 bg-white rounded border text-center">
                          <p className="text-muted-foreground">Available for WO</p>
                          <p className={`font-bold text-sm ${a.available_for_new_wo > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{a.available_for_new_wo}</p>
                        </div>
                      </div>
                      {a.rejected > 0 && <p className="text-[10px] text-red-600">Rejected in QC: {a.rejected}</p>}
                      {a.quarantined > 0 && <p className="text-[10px] text-amber-600">Quarantined (MRB): {a.quarantined}</p>}
                      {/* WO allocation breakdown */}
                      {a.wo_allocations?.length > 0 && (
                        <div className="mt-1">
                          <p className="text-[10px] font-semibold text-muted-foreground mb-1">Work Order Breakdown:</p>
                          <table className="w-full text-[10px]">
                            <thead><tr className="border-b bg-white">
                              <th className="text-left p-1 font-medium">WO#</th>
                              <th className="text-center p-1 font-medium">Status</th>
                              <th className="text-center p-1 font-medium">Reserved</th>
                              <th className="text-center p-1 font-medium">Consumed</th>
                              <th className="text-center p-1 font-medium">Pending</th>
                            </tr></thead>
                            <tbody>
                              {a.wo_allocations.map((wa, j) => (
                                <tr key={j} className="border-b last:border-0">
                                  <td className="p-1 font-mono font-medium">{wa.wo_number}</td>
                                  <td className="p-1 text-center">
                                    <Badge variant="outline" className={`text-[9px] ${WO_STATUS_COLORS[wa.status] || ''}`}>{wa.status}</Badge>
                                  </td>
                                  <td className="p-1 text-center">{wa.reserved}</td>
                                  <td className="p-1 text-center font-medium text-amber-700">{wa.consumed}</td>
                                  <td className="p-1 text-center text-muted-foreground">{wa.pending_consumption}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          {/* ===== WORK ORDERS ===== */}
          {data.work_orders?.length > 0 && (
            <Card>
              <SectionHeader title="Work Orders" icon={Factory} sectionKey="wo" count={data.work_orders.length} color="text-orange-700" />
              {expandedSections.wo && (
                <CardContent className="p-3 pt-0 space-y-2">
                  {data.work_orders.map((wo, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-xs font-mono font-bold">{wo.wo_number}</span>
                          <ArrowRight className="w-3 h-3 inline mx-1 text-muted-foreground" />
                          <span className="text-xs font-medium">{wo.item_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-[10px] ${WO_STATUS_COLORS[wo.status] || ''}`}>{wo.status}</Badge>
                          <span className="text-xs">
                            <span className="font-bold">{wo.produced_quantity}</span>
                            <span className="text-muted-foreground">/{wo.quantity} produced</span>
                          </span>
                        </div>
                      </div>
                      {wo.bom?.length > 0 && (
                        <p className="text-[10px] text-muted-foreground mb-1.5">
                          BOM: {wo.bom.map(b => `${b.item_name} x${b.quantity}`).join(', ')}
                        </p>
                      )}
                      <table className="w-full text-[10px]">
                        <thead><tr className="border-b bg-white">
                          <th className="text-left p-1 font-medium">Source Challan</th>
                          <th className="text-left p-1 font-medium">Item</th>
                          <th className="text-center p-1 font-medium">Reserved</th>
                          <th className="text-center p-1 font-medium">Consumed</th>
                          <th className="text-center p-1 font-medium">Pending</th>
                        </tr></thead>
                        <tbody>
                          {wo.challan_line_items.map((cli, j) => (
                            <tr key={j} className="border-b last:border-0">
                              <td className="p-1 font-mono">{cli.challan_number} [#{cli.line_index + 1}]</td>
                              <td className="p-1">{cli.item_name}</td>
                              <td className="p-1 text-center">{cli.reserved || cli.selected_quantity}</td>
                              <td className="p-1 text-center font-medium text-amber-700">{cli.actual_consumed}</td>
                              <td className="p-1 text-center text-muted-foreground">{cli.pending_consumption}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          {/* ===== CURRENT BALANCES ===== */}
          {data.current_balances?.length > 0 && (
            <Card>
              <SectionHeader title="Current Inventory Balance" icon={Package} sectionKey="balances" count={data.current_balances.length} color="text-emerald-700" />
              {expandedSections.balances && (
                <CardContent className="p-0 pt-0">
                  <table className="w-full text-xs">
                    <thead><tr className="border-t bg-muted/30">
                      <th className="text-left p-2 font-medium text-muted-foreground">Item</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Godown</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Quantity</th>
                    </tr></thead>
                    <tbody>
                      {data.current_balances.map((b, i) => (
                        <tr key={i} className="border-t hover:bg-muted/20">
                          <td className="p-2">
                            <p className="font-medium">{b.item_name}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{b.item_unique_id}</p>
                          </td>
                          <td className="p-2">{b.godown_name} <span className="text-[10px] text-muted-foreground">({b.godown_code})</span></td>
                          <td className="p-2 text-center font-bold">{b.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          )}

          {/* ===== DISPATCH ===== */}
          {data.dispatch_info?.length > 0 && (
            <Card>
              <SectionHeader title="Dispatch" icon={Truck} sectionKey="dispatch" count={data.dispatch_info.length} color="text-teal-700" />
              {expandedSections.dispatch && (
                <CardContent className="p-0 pt-0">
                  <table className="w-full text-xs">
                    <thead><tr className="border-t bg-muted/30">
                      <th className="text-left p-2 font-medium text-muted-foreground">DC#</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Manifest</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Item</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Qty</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Status</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Date</th>
                    </tr></thead>
                    <tbody>
                      {data.dispatch_info.map((d, i) => (
                        <tr key={i} className="border-t hover:bg-muted/20" data-testid={`trace-dispatch-row-${i}`}>
                          <td className="p-2 font-mono font-medium">{d.dc_number}</td>
                          <td className="p-2 font-mono text-muted-foreground">{d.manifest_number || '-'}</td>
                          <td className="p-2">{d.item_name}</td>
                          <td className="p-2 text-center font-medium">{d.quantity}</td>
                          <td className="p-2 text-center">
                            {d.status === 'cancelled' ? (
                              <Badge className="text-[10px] bg-red-100 text-red-700 border border-red-200" data-testid={`trace-dispatch-cancelled-${i}`}>Cancelled</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">{d.status}</Badge>
                            )}
                          </td>
                          <td className="p-2 text-muted-foreground">{d.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          )}

          {/* ===== MRB CASES ===== */}
          {data.mrb_cases?.length > 0 && (
            <Card>
              <SectionHeader title="MRB Cases" icon={AlertTriangle} sectionKey="mrb" count={data.mrb_cases.length} color="text-red-600" />
              {expandedSections.mrb && (
                <CardContent className="p-0 pt-0">
                  <table className="w-full text-xs">
                    <thead><tr className="border-t bg-muted/30">
                      <th className="text-left p-2 font-medium text-muted-foreground">Item</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Qty</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Status</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Disposition</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Location</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Remarks</th>
                    </tr></thead>
                    <tbody>
                      {data.mrb_cases.map((m, i) => (
                        <tr key={i} className="border-t hover:bg-muted/20">
                          <td className="p-2 font-medium">{m.item_name}</td>
                          <td className="p-2 text-center font-medium">{m.quantity}</td>
                          <td className="p-2 text-center"><Badge variant="outline" className="text-[10px]">{m.status}</Badge></td>
                          <td className="p-2 text-center"><Badge variant="outline" className="text-[10px]">{m.disposition}</Badge></td>
                          <td className="p-2">{m.godown_name}</td>
                          <td className="p-2 text-muted-foreground">{(m.qc_remarks || '').slice(0, 50)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          )}

          {/* ===== TRANSACTIONS ===== */}
          {data.transactions?.length > 0 && (
            <Card>
              <SectionHeader title="Inventory Transactions" icon={Clock} sectionKey="transactions" count={data.transactions.length} color="text-slate-600" />
              {expandedSections.transactions && (
                <CardContent className="p-0 pt-0">
                  <table className="w-full text-xs">
                    <thead><tr className="border-t bg-muted/30">
                      <th className="text-left p-2 font-medium text-muted-foreground">Type</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Item</th>
                      <th className="text-center p-2 font-medium text-muted-foreground">Qty</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Godown</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Reference</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Remarks</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Date</th>
                    </tr></thead>
                    <tbody>
                      {data.transactions.map((t, i) => (
                        <tr key={i} className="border-t hover:bg-muted/20">
                          <td className="p-2">
                            <Badge variant="outline" className={`text-[9px] ${TXN_TYPE_COLORS[t.transaction_type] || ''}`}>
                              {TXN_TYPE_LABELS[t.transaction_type] || t.transaction_type}
                            </Badge>
                          </td>
                          <td className="p-2">{t.item_name}</td>
                          <td className="p-2 text-center font-medium">{t.quantity}</td>
                          <td className="p-2">{t.godown_name || '-'}</td>
                          <td className="p-2 font-mono text-[10px]">{t.reference_number}</td>
                          <td className="p-2 text-[10px] text-muted-foreground max-w-[200px] truncate">{t.remarks}</td>
                          <td className="p-2 text-[10px] text-muted-foreground">{t.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          )}

          {/* Empty state */}
          {!data.challan_lines?.length && !data.work_orders?.length && !data.transactions?.length && (
            <Card>
              <CardContent className="p-8 text-center">
                <Search className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No results found for "{data.search_term}"</p>
                <p className="text-xs text-muted-foreground mt-1">Try searching by Customer WO#, Trace ID, Work Order#, or Challan#</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
